import { useContext, useEffect, useState } from "react"
import "./css/ViewSubmissions.css"
import axios from "axios"
import api from "../config/backend"
import PropTypes from 'prop-types'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import LastPageIcon from '@mui/icons-material/LastPage'
import { styled } from '@mui/material/styles'
import TableHead from '@mui/material/TableHead'
import { useNavigate } from "react-router"
import { DashboardState } from "./Dashboard"
import { BiArrowBack } from "react-icons/bi"
import { ArrowBackIos, ChevronRight } from "@mui/icons-material"
import { Link } from "react-router-dom"
import { Button } from "@mui/material"

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        color: "#212121",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}))

function TablePaginationActions(props) {
    const theme = useTheme()
    const { count, page, rowsPerPage, onPageChange } = props

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0)
    }

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1)
    }

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1)
    }

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
    }

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton
                onClick={handleFirstPageButtonClick}
                disabled={page === 0}
                aria-label="first page"
            >
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton
                onClick={handleBackButtonClick}
                disabled={page === 0}
                aria-label="previous page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton
                onClick={handleNextButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="next page"
            >
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton
                onClick={handleLastPageButtonClick}
                disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                aria-label="last page"
            >
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    )
}

TablePaginationActions.propTypes = {
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
}

const ViewSubmissions = () => {
    const [datadetails, setDatadetails] = useState([])
    const [submissions, setSubmissions] = useState([])
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(5)

    const { state, actions } = useContext(DashboardState)

    const navigate = useNavigate()

    const token = localStorage.getItem("_crm__token")

    const fetchSubmissions = async () => {
        const { data, status } = await axios.post(`${api}/Form/fetch-submissions`, { token })

        if (data.success) {
            setSubmissions(data.submissions)
        } else {
            console.log({ data })
        }
    }

    const fetchDetails = async () => {
        const { data, status } = await axios.post(`${api}/Form/fetch-details`, { token })
        
        if (data.success) {
            setDatadetails(data.form.inputs)
        } else {
            console.log({ data })
        }
    }

    // Avoid a layout jump when reaching the last page with empty submissions.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - submissions.length) : 0

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    useEffect(() => {
        fetchSubmissions()
        fetchDetails()
    }, [])

    return (
        <div>
            <div className="app-page-form-submission">
                <div className="app-page-form__header">
                    <div className="app-page-form__header--title">
                        <h4>Form submissions</h4>
                        <ul className="app-breadcrumb-list">
                            <li className="app-breadcrumb-listitem"><Link to="/forms">Form</Link></li>
                            <li className="app-breadcrumb-listitem app-breadcrumb-listitem--icon"><ChevronRight/></li>
                            <li className="app-breadcrumb-listitem">Form submissions</li>
                        </ul>
                    </div>
                    <div className="app-page-form__header--actions">
                        <Button variant="contained" className="app-btn-contained app-btn-primary" onClick={() => { navigate("/forms") }}><ArrowBackIos style={{fontSize: "14px"}}/> Back</Button>
                    </div>
                </div>
                <div className="app-page-form-submission__body">
                    <div className="app-page-form-submission__table">
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 700 }} aria-label="customized table">
                                <TableHead>
                                    <TableRow>
                                        {datadetails && datadetails.map((item, index) => {
                                            return item.active === true ? (
                                                <StyledTableCell key={index} align="center">
                                                    {item.fieldname}
                                                </StyledTableCell>
                                            ) : undefined
                                        })}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {(rowsPerPage > 0
                                        ? submissions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        : submissions
                                    ).map((row, index) => {
                                        console.log({ row })
                                        return (
                                            <StyledTableRow key={index}>
                                                {datadetails.map((item, cellIndex) => {
                                                    const fieldname = String(item.fieldname).toLowerCase()

                                                    console.log({row, fieldname, selected: row[fieldname]})

                                                    const customdata = row.custom.find(item => item.fieldname === fieldname)

                                                    return item.active === true ? (
                                                        <StyledTableCell key={cellIndex} align="center">
                                                            {row[fieldname] ? row[fieldname] : customdata && customdata.value}
                                                        </StyledTableCell>
                                                    ) : undefined
                                                })}
                                            </StyledTableRow>
                                        )
                                    })}
                                </TableBody>

                                <TableFooter>
                                    <TableRow>
                                        <TablePagination
                                            rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                            colSpan={3}
                                            count={submissions.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            SelectProps={{
                                                inputProps: {
                                                    'aria-label': 'rows per page',
                                                },
                                                native: true,
                                            }}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            ActionsComponent={TablePaginationActions}
                                        />
                                    </TableRow>
                                </TableFooter>
                            </Table>
                        </TableContainer>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ViewSubmissions
